import { observable, makeObservable, action } from "mobx";
import { ChangeEvent } from "react";
import { RootStore } from "src/stores/RootStore";
import { IInvoiceFile } from "./InvoiceTypes";
import { v4 as uuidv4 } from "uuid";
import { INVOICE_FILE_STATE, INVOICE_FILE_TYPE, INVOICE_PROPERTY } from "./InvoiceEnums";
import { Ticket } from "src/stores/TicketStore";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";

export interface FileHTMLInputEvent extends ChangeEvent {
    target: HTMLInputElement & EventTarget;
}

const dataTypes = ["application/pdf"];

export class InvoiceFilePreviewStore {
    rootStore: RootStore;

    isDraggingActive: boolean = false;

    isDraggingOverMainWindow: boolean = false;

    isLoadingPDFFile: boolean = false;
    isFileDeleting: boolean = false;

    currentFileInputEvent: FileHTMLInputEvent | undefined = undefined;
    displayOverrideExistingDataModal: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isDraggingActive: observable,
            currentFileInputEvent: observable,
            displayOverrideExistingDataModal: observable,
            isLoadingPDFFile: observable,
            isFileDeleting: observable,
            isDraggingOverMainWindow: observable,

            handleFileInputChange: action,
            extractQrCodeDataAndUpdateInvoiceStore: action,
            setCurrentFileInputEvent: action,
            setDisplayOverrideExistingDataModal: action,
            setIsLoadingPDFFile: action,
            setIsFileDeleting: action,
            setIsDraggingOverMainWindow: action,
            setIsDraggingActive: action,
            toggleDisplayOverrideExistingDataModal: action,
            handleClickOverrideExistingDataYes: action,
            handleClickOverrideExistingDataNo: action,
            handleDeleteFile: action,
            downloadFile: action
        });
    }

    setIsDraggingActive = (dragging: boolean) => {
        this.isDraggingActive = dragging;
    };

    setCurrentFileInputEvent = (event: FileHTMLInputEvent) => {
        this.currentFileInputEvent = event;
    };

    setDisplayOverrideExistingDataModal = (display: boolean) => {
        this.displayOverrideExistingDataModal = display;
    };

    setIsLoadingPDFFile = (loading: boolean) => {
        this.isLoadingPDFFile = loading;
    };

    setIsFileDeleting = (deleting: boolean) => {
        this.isFileDeleting = deleting;
    };

    setIsDraggingOverMainWindow = (dragging: boolean) => {
        this.isDraggingOverMainWindow = dragging;
    };

    toggleDisplayOverrideExistingDataModal = () => {
        this.setDisplayOverrideExistingDataModal(!this.displayOverrideExistingDataModal);
    };

    handleClickOverrideExistingDataYes = () => {
        this.setDisplayOverrideExistingDataModal(false);
        if (this.currentFileInputEvent) {
            this.handleFileInputChange({
                displayOverrideExistingDataModal: false,
                overrideData: true
            })(this.currentFileInputEvent);
        }
    };

    handleClickOverrideExistingDataNo = () => {
        this.setDisplayOverrideExistingDataModal(false);
        if (this.currentFileInputEvent) {
            this.handleFileInputChange({
                displayOverrideExistingDataModal: false,
                overrideData: false
            })(this.currentFileInputEvent);
        }
    };

    handleDeleteFile = async (file: IInvoiceFile | undefined) => {
        this.setIsFileDeleting(true);

        const invoiceStore = this.rootStore.invoiceStore;

        if (file && file.fileId && file.state === INVOICE_FILE_STATE.LOADED_FROM_DB) {
            await invoiceStore.currentInvoice?.deleteImage(file.fileId);
        }

        const invoiceFile: IInvoiceFile = {
            state: INVOICE_FILE_STATE.NEW
        };

        invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.INVOICE_FILE, invoiceFile);

        this.setIsFileDeleting(false);
    };

    extractQrCodeDataAndUpdateInvoiceStore = async (file: File, overrideData: boolean) => {
        const uiStore = this.rootStore.uiStore;
        const invoiceStore = this.rootStore.invoiceStore;

        const fileUrl = URL.createObjectURL(file);

        const invoiceFile: IInvoiceFile = {
            fileId: uuidv4(),
            invoiceFileId: uuidv4(),
            name: file.name,
            url: fileUrl,
            type: INVOICE_FILE_TYPE.INVOICE,
            mimetype: file.type,
            extension: file.name.split(".").pop(),
            fileDate: Ticket.formatDateForImage(new Date(file.lastModified)),
            state: INVOICE_FILE_STATE.HAS_CHANGED
        };

        const isPDFFile = dataTypes.includes(file.type);

        if (isPDFFile) {
            const reader = new FileReader();

            const readFileAsBase64 = (file: File) => {
                return new Promise<string>((resolve, reject) => {
                    reader.readAsDataURL(file);
                    reader.onload = () => {
                        const fileBase64String = reader.result as string;
                        const base64String = fileBase64String.split(",")[1];
                        resolve(base64String);
                    };
                    reader.onerror = (error) => {
                        reject(error);
                    };
                });
            };

            try {
                const base64String = await readFileAsBase64(file);
                invoiceFile.base64String = base64String;

                const extractQrCodeDataFromPDF = await invoiceStore.extractQrCodeDataFromPDF(base64String);

                if (extractQrCodeDataFromPDF) {
                    invoiceStore.mapExtractedDataOnCurrentInvoice(extractQrCodeDataFromPDF, overrideData);
                }

                invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.INVOICE_FILE, invoiceFile);
            } catch (error) {
                console.error("Error reading file:", error);
            }
        } else {
            uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.form.invoice.preview.file_selection_error"),
                MessageType.ERROR
            );
        }
    };

    handleFileInputChange =
        (overrideDataVariables: { displayOverrideExistingDataModal: boolean; overrideData: boolean }) =>
        async (event: FileHTMLInputEvent) => {
            const uiStore = this.rootStore.uiStore;

            uiStore.setIsResizeObserverDisabled(false);

            if (overrideDataVariables.displayOverrideExistingDataModal) {
                this.setCurrentFileInputEvent(event);
                this.setDisplayOverrideExistingDataModal(true);
            } else {
                this.setIsLoadingPDFFile(true);

                if (event?.target?.files) {
                    const files = Array.from(event.target.files);

                    if (files.length > 0) {
                        const file: File = files[0];
                        await this.extractQrCodeDataAndUpdateInvoiceStore(file, overrideDataVariables.overrideData);
                    }

                    this.setIsLoadingPDFFile(false);
                }
            }
        };

    downloadFile = async (fileId: string) => {
        const downloadedFile = await this.rootStore.invoiceStore.downloadInvoiceFile(fileId);

        if (downloadedFile) {
            try {
                let fileBlob = await downloadedFile.data;
                const objUrl = window.URL.createObjectURL(fileBlob);

                const pdfFileInfo = await this.rootStore.splitPdfStore.getPdfInfo(fileId);

                // Trigger the download
                const a = document.createElement("a");
                a.href = objUrl;
                a.download = pdfFileInfo
                    ? pdfFileInfo.name
                    : i18next.t("screens.kredi_flow.form.invoice.preview.file_download_default_name");
                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (error) {
                console.error("Error downloading file:", error);
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.form.invoice.preview.file_download_error"),
                    MessageType.ERROR
                );
            }
        }
    };
}
